import { useRef } from 'react'

import { Flex, InfoIcon, Link, Text } from '@buffer-mono/popcorn'

import { EmojiPickerButton } from '../../components/EngagementReplyFormFooter'
import { ReplyForm } from '../../components/ReplyForm'
import { SavedRepliesPopover } from '../../components/SavedRepliesPopover'
import type { Service } from '../../graphqlEnums'
import { instagramConversationUrl } from './replyWindow'
import { DM_CHARACTER_LIMIT, useMessageDraft } from './useMessageDraft'
import { useReplyWindowOpen } from './useReplyWindowOpen'

import styles from './MessageComposer.module.css'

import type { JSX } from 'react'

type MessageComposerProps = {
  conversationId: string
  service: Service
  /** The counterpart's handle, for the link into the Instagram app. */
  counterpartUsername: string | null
  /** When the network stops letting us reply; `null` when it does not. */
  replyWindowExpiresAt: string | null
  /** Reference time for the window check. Defaults to now; tests pin it. */
  now?: Date
}

/**
 * The reply form under the thread, per the DMs Figma `_input v2`: the shared
 * reply form on its card surface, with the message field and a footer of
 * emoji picker, saved replies and send. Sending is not wired yet, so the send
 * button stays disabled; the rest of the form works so the draft can be
 * written and shaped.
 *
 * When the network's reply window has closed the form gives way to a notice
 * that points at the conversation in the Instagram app, since Buffer can no
 * longer reply there.
 */
export function MessageComposer({
  conversationId,
  service,
  counterpartUsername,
  replyWindowExpiresAt,
  now,
}: MessageComposerProps): JSX.Element {
  const replyWindowOpen = useReplyWindowOpen(replyWindowExpiresAt, now)

  if (!replyWindowOpen) {
    return <ReplyWindowClosed counterpartUsername={counterpartUsername} />
  }

  // One form per conversation: the text typed for one person must never
  // render under another, so a new conversation starts from an empty draft.
  return <ComposerForm key={conversationId} service={service} />
}

function ComposerForm({
  service,
}: Pick<MessageComposerProps, 'service'>): JSX.Element {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const draft = useMessageDraft({ service, textareaRef })

  return (
    <ReplyForm.Root
      surface="card"
      ariaLabel="Write a message"
      onSubmit={(event): void => event.preventDefault()}
    >
      <ReplyForm.Field
        textareaRef={textareaRef}
        replyText={draft.text}
        setReplyText={draft.setText}
        insertedRange={draft.insertedRange}
        onHighlightComplete={draft.clearInsertedRange}
        maxLength={DM_CHARACTER_LIMIT}
        service={service}
        ariaLabel="Message"
        placeholder="Write a message..."
        rows={1}
      />
      <ReplyForm.Footer>
        <ReplyForm.FooterStart>
          <EmojiPickerButton
            emojiPickerRef={draft.emojiPickerRef}
            onEmojiSelect={draft.insertEmoji}
          />
          <SavedRepliesPopover
            savedRepliesPopoverRef={draft.savedRepliesRef}
            handleInsertSavedReply={draft.insertSavedReply}
          />
        </ReplyForm.FooterStart>
        <ReplyForm.FooterEnd>
          <ReplyForm.Submit label="Send" disabled />
        </ReplyForm.FooterEnd>
      </ReplyForm.Footer>
    </ReplyForm.Root>
  )
}

/**
 * Per the Figma "Replying blocked" frame: the window Meta gives third-party
 * apps has passed, so the reply has to happen in the Instagram app. The
 * conversation stays in the inbox.
 */
function ReplyWindowClosed({
  counterpartUsername,
}: Pick<MessageComposerProps, 'counterpartUsername'>): JSX.Element {
  return (
    <Flex justify="center" className={styles.closed}>
      <Text size="sm" color="subtle" icon={<InfoIcon size="small" />}>
        7 day reply window for this conversation expired.{' '}
        {counterpartUsername && (
          <Link
            href={instagramConversationUrl(counterpartUsername)}
            external
            className={styles.closedLink}
          >
            Reply on Instagram
          </Link>
        )}
      </Text>
    </Flex>
  )
}
